/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { createAsyncThunk } from '@reduxjs/toolkit';
import { StatusCodes } from 'http-status-codes';
import urlJoin from 'proper-url-join';

import * as API from '@ibm-watson/shared/modules/ia/api/instance';
import {
  addErrorMessage,
  accessDenied
} from '@ibm-watson/shared/modules/ia/store/messages/actions';
import { StoreActionType } from '../../../../store/types';
import type { ResetAction } from '../../../../store/types';
import { ApiStatus } from '../../../../ts';
import type { InstanceData } from '../../../../ts/instance';
import type { TrialData } from '../../../../ts/plan';
import type {
  InstancePreferences,
  UserPreferences
} from '../../../../ts/preferences';
import type { CreateAsyncThunk } from '../../../../ts/state';
import { setConvoSkillProviderId } from '../../../actions/store/convo-skills/reducer';
import { sendClearAssistantsEvent } from '../../hooks/assistantsSwitcherUtils';
import { resetSession } from '../session/actions';
import { selectToolingData } from './selectors';

/**
 * This method only updates the `plus_trial` field in `tooling_data`.
 * It uses `updateInstance` so you can do it too. No need to use this method.
 * @deprecated use `updateInstance` method instead
 * @param {*} plus_trial The `start_date` and number of `extensions` to update
 * @returns An async action
 */
export const updatePlusTrialData =
  (plus_trial: TrialData) => async (dispatch, getState) => {
    const toolingData = selectToolingData(getState());
    return dispatch(
      updateInstance({ tooling_data: { ...toolingData, plus_trial } })
    );
  };

interface GetInstanceArgs {
  // If present it is a query string (beginning with a "?" representing the
  // query parameters specified in the Tooling URL.
  search?: string;
}

export const getInstance = createAsyncThunk<
  InstanceData,
  GetInstanceArgs,
  CreateAsyncThunk
>(
  'instance/getInstance',
  async ({ search } = {}, { dispatch }) => {
    try {
      const instance = await API.getInstance(search);

      // Check for a conversational skill section (which would indicate that
      // this version of Store supports only one provider); if there is one,
      // save its provider ID globally in the convo skill slice.
      const providerId = instance.conversational_skill_provider?.provider_id;

      if (providerId) {
        dispatch(setConvoSkillProviderId(providerId));
      }

      return instance;
    } catch (error) {
      const status = error?.response?.status || error.status;
      if (status === StatusCodes.FORBIDDEN) {
        dispatch(accessDenied(error.transactionId));
        throw new Error('Unauthorized');
      } else {
        throw error;
      }
    }
  },
  {
    condition: (_, { getState }) => {
      const { instance } = getState();
      if (instance?.getStatus === ApiStatus.LOADING) {
        // Fetching in progress, don't need to re-fetch
        return false;
      }
    }
  }
);

export const updateInstance = createAsyncThunk<
  InstanceData,
  Pick<InstanceData, 'tooling_data'>,
  CreateAsyncThunk
>('instance/update', async (data, { dispatch }) => {
  try {
    await API.postInstance(data);

    // Since store doesn't return the updated object we need to fetch it again
    return await API.getInstance();
  } catch (error) {
    dispatch(addErrorMessage(error));
    throw error;
  }
});

export const updateUserPreferences = createAsyncThunk<
  InstanceData,
  UserPreferences,
  CreateAsyncThunk
>('instance/updateUserPreferences', async (data, { dispatch }) => {
  try {
    await API.setUserPreferences({ preferences: data });

    // refetch the full instance data
    return await API.getInstance();
  } catch (error) {
    dispatch(addErrorMessage(error));
    throw error;
  }
});

export const updateInstancePreferences = createAsyncThunk<
  InstancePreferences,
  InstancePreferences,
  CreateAsyncThunk
>('instance/updateInstancePreferences', async (data, { dispatch }) => {
  try {
    const { preferences } = await API.setInstancePreferences({
      preferences: data
    });

    return preferences;
  } catch (error) {
    dispatch(addErrorMessage(error));
    throw error;
  }
});

interface SwitchExperienceData {
  new_ia: boolean;
  instanceId: string;
  navigate: (url: string) => void;
}

/**
 * Action that will switch the experience from Classic IA to New IA or
 * vice versa. It will:
 *
 *   1. Reset the "session" slice to "freeze" the application (i.e. cause the
 * instance routes to render the loading page) until the process is complete.
 *   2. Navigate to the "/home" route (which is common to both experiences)
 *   3. Invoke the API that persists the value of the experience for the
 * specified instance for the current user.
 *   4. Reset the Redux store to its original initialized state (while still
 * preserving information that is common across experiences, like the user and
 * instance information).
 *
 * @param new_ia True if switching to New IA, false if switching to Classic IA
 * @param instanceId The instance ID whose experience is being switched
 * @param navigate Function that will change to the URL to the value specified
 */
export const switchExperience = createAsyncThunk<
  void,
  SwitchExperienceData,
  CreateAsyncThunk
>(
  'instance/switchExperience',
  async ({ new_ia, instanceId, navigate }, { dispatch }) => {
    try {
      // Dispatch the action that resets the session slice and "freezes" the
      // application (showing a loading screen) until the process is complete.
      dispatch(resetSession());

      // Before resetting the store, reset the path to the "/home" route
      // (which is common across both experiences) to avoid a flash of the
      // "Page not found" error page.
      navigate(urlJoin(encodeURIComponent(instanceId), '/home'));

      await dispatch(updateUserPreferences({ new_ia })).unwrap();
    } catch (error) {
      dispatch(addErrorMessage(error));
      throw error;
    } finally {
      // In case we are loaded in WXO, send a global event that lets the WXO
      // host page clear out the list of assistants it knows about.
      // In New IA, WXO will show a drop-down on its header with the list
      // of New IA assistants; when switching experiences, however, said
      // drop-down should be removed, since it should not be shown on
      // Classic IA.
      sendClearAssistantsEvent();

      // Finalize the process to reset the Redux store.
      // We do this in a "finally" block to ensure that the session slice is
      // re-initialized even if there are errors modifying the user
      // preferences.
      const action: ResetAction = {
        type: StoreActionType.RESET_REDUX,
        payload: {
          // Ensure that after being initialized, Redux will have the most
          // accurate information as to whether the instance is New IA or
          // Classic IA.
          new_ia
        }
      };

      // Dispatch the action (defined at the global reducer) that resets the
      // Redux store to its initial state.
      dispatch(action);
    }
  }
);
